import { formatCurrency, formatMegawatts, formatPercentage } from '@/util/formatters'
import { type TableColumnCtx } from 'element-plus'
import { DateTime } from 'luxon'

export const currencyFormatter = function (row: any, column: TableColumnCtx<any>, cellValue: any) {
  if (cellValue != null && cellValue != undefined) {
    return formatCurrency(Number(cellValue))
  }
  return '-'
}

export const megawattFormatter = function (row: any, column: TableColumnCtx<any>, cellValue: any) {
  if (cellValue != null) {
    return formatMegawatts(cellValue)
  }
  return '-'
}

export const uptimeFormatter = function (row: any, column: TableColumnCtx<any>, cellValue: any) {
  if (cellValue != null) {
    return formatPercentage(cellValue)
  }
  return '-'
}

export const enabledFormatter = (value: boolean) => {
  return value ? 'Enabled' : 'Disabled'
}

/**
 * Formats an Element Plus table cell as a phone number.
 *
 * If the cell value is a valid US phone number with country code, formats the value.
 * Otherwise, returns original value.
 *
 * If the cell is empty, falls back to '-'.
 *
 * @param row table row index.
 * @param column table column index.
 * @param phoneNumber cell value.
 * @returns formatted phone number based on cell value.
 */
export const phoneFormatter = (row: any, column: any, phoneNumber: string) => {
  if (!phoneNumber) return '-'
  if (!/^\+\d{11}$/.test(phoneNumber)) return phoneNumber

  // Format the numeric string into +1 (XXX) XXX-XXXX
  return `+1 (${phoneNumber.substring(2, 5)}) ${phoneNumber.substring(5, 8)}-${phoneNumber.substring(8)}`
}

/**
 * dateFormatter, but for js Date datatype.
 */
export const jsDateFormatter = (row: any, column: any, cellValue: Date | null) => {
  if (!cellValue) return '-'
  return dateFormatter(row, column, cellValue.toISOString())
}

/**
 * Luxon date formatter.
 */
export const luxonDateFormatter = (row: any, column: any, cellValue: DateTime | null) => {
  if (!cellValue?.isValid) return '-'
  return cellValue.toFormat('MM/dd/yyyy')
}

/**
 * Formats an Element Plus table date cell.
 *
 * @param row table row.
 * @param column table column.
 * @param cellValue cell value.
 * @returns formatted cell value or '-' if empty.
 */
export const dateFormatter = (row: any, column: any, cellValue: string) => {
  const datetime = DateTime.fromISO(cellValue)
  return luxonDateFormatter(row, column, datetime)
}

/**
 * Formats an Element Plus table date time cell value.
 *
 * @param row table row.
 * @param column table column.
 * @param cellValue cell value.
 * @returns formatted cell value or '-' if empty.
 */
export const dateTimeFormatter = (row: any, column: any, cellValue: string) => {
  const datetime = DateTime.fromISO(cellValue)
  return datetime.isValid ? datetime.toFormat('MM/dd/yyyy HH:mm:ss ZZZZ') : '-'
}

/**
 * Formats a possibly null or undefined value with a fallback.
 *
 * @param row table row.
 * @param column table column.
 * @param cellValue cell value.
 */
export const optionalValueFallbackFormatter = (row: any, column: any, cellValue: string) => {
  return cellValue ? cellValue : '-'
}
